// programUtils.js


/**
 * Validates the upper limit, lower limit, and target value for a modality against default modality thresholds.
 * 
 * @param {Object} modality - The modality object containing limits and target value.
 * @param {Array<Object>} defaultModList - List of default modality threshold objects.
 * @returns {boolean} - Returns `true` if the limits are invalid, otherwise `false`.
 */
export const checkModalityThresholds = (modality, defaultModList, allDevices) => {
    const {deviceId, deviceModalityId, upperLimit, lowerLimit, targetValue} = modality;
    const modalityId = getModalityId(deviceModalityId, deviceId, allDevices);
    let defaultModValues = defaultModList.find((item)=>item.modalityId === modalityId);
    if (upperLimit === 0 && lowerLimit === 0 && targetValue === 0) {
        return false;
    }
    if (upperLimit !== 0 && lowerLimit !== 0) {
        if (lowerLimit >= upperLimit) {
            return true;
        }
        if (targetValue !== 0 && (targetValue > upperLimit || targetValue < lowerLimit)) {
            return true;
        }
    }
    if (defaultModValues?.maximumHigh != null &&
        defaultModValues?.minimumHigh != null &&
        defaultModValues?.maximumLow != null &&
        defaultModValues?.minimumLow != null) {

        if (lowerLimit !== 0) {
            if (lowerLimit < defaultModValues.minimumLow || lowerLimit > defaultModValues.maximumLow) {
                return true;
            }
        }

        if (upperLimit !== 0) {
            if (upperLimit > defaultModValues.maximumHigh || upperLimit < defaultModValues.minimumHigh) {
                return true;
            }
        }
    }
    return false
};

/**
 * @returns {string} - The ISO string representing today's date at 9:00 AM UTC.
 */
export const getInitialStartTime = () => {
    const currentDate = new Date();
    currentDate.setHours(9,0,0,0);
    let utcTimeStamp = currentDate.toISOString();
    return utcTimeStamp;
};

/**
 * Converts a UTC timestamp to local time and extracts the hour and AM/PM interval.
 * @param {string} dataStart - The UTC timestamp string to convert.
 * @returns {{startInt: number, interval: string}} - 
 * An object containing:
 *  - `startInt`: The hour in 12-hour format (1-12).
 *  - `interval`: A string representing "AM" or "PM".
 */
export const initStartTime = (dataStart) => {
    let startStr = dataStart;
    let startInt = 0;
    let interval = "AM";
    if (startStr !== ""){
        if (!startStr.includes('Z')) {
            startStr = startStr + '.000Z';
        }
        const localDate = new Date(startStr);
        const hour = localDate.getHours();
        startInt = hour % 12 || 12;
        interval = hour >= 12 ? "PM" : "AM";
    }
    return {startInt:startInt, interval:interval};
};

/**
 * Converts a 12-hour format time with AM/PM into a UTC ISO timestamp string.
 * @param {number} hour12 - The hour in 12-hour format (1-12).
 * @param {string} period - Either "AM" or "PM" indicating the time period.
 * @returns {string} - The UTC ISO timestamp representing today's date at the given time, or an empty string if invalid.
 */
export const convertStartTime = (hour12, period) => {
    let hour24 = hour12;
    if (hour12 === -1) {
        return "";
    }
    if (period === 'PM' && hour12 !== 12) {
        hour24 += 12; // Add 12 hours for PM
    } else if (period === 'AM' && hour12===12){
        hour24 = 0;
    }
    // Construct the ISO string
    const currentDate = new Date();
    currentDate.setHours(hour24,0,0,0);
    let utcTimeStamp = "";
    try {
        utcTimeStamp = currentDate.toISOString();
    } catch (error) {
        console.error(error);
    }
    return utcTimeStamp;
};


/**
 * Returns a modality id for a deviceModalityId using a list of all program devices.
 * @param {number} deviceModalityId - The unique device-modalityId.
 * @param {number} deviceId - The unique device id.
 * @param {Array<Object>} allDevices - List of all available devices for a group
 * @returns {number} - returns the modality id for a specific deviceModalityId
 */
export const getModalityId = (deviceModalityId, deviceId, allDevices) => {
    const foundDevice = allDevices.find((device)=> device.deviceId === deviceId);
    if (foundDevice) {
        const allDeviceModalities = foundDevice.modalities;
        const foundModality = allDeviceModalities.find((mod)=> mod.deviceModalityId === deviceModalityId);
        return foundModality?.modalityId;
    }
    return null;
};


/**
 * Initializes selected questionnaires by matching program questionnaires with available questionnaires.
 * @param {Array<Object>} programQuestionnaires - List of questionnaires from a program
 * @param {Array<Object>} allQuestionnaires - List of all available questionnaires
 * @returns {Array<Object>} - Array of selected questionnaires with complete data
 */
export const initSelectedQuestionnaires = (programQuestionnaires, allQuestionnaires) => {
    // check to see if questionnaire already exists in the state,
    const tempQs = [];
    programQuestionnaires.forEach((item)=> {
        let existingQIndex = allQuestionnaires.findIndex(q => q.questionnaireId === item.questionnaireId);
        if (existingQIndex !== -1){
            // if addProgram.questionnaire has a match in the questionnaire list, add to selectedQuestionnaires
            tempQs.push(allQuestionnaires[existingQIndex]);
        } else {
            tempQs.push({
                id: item.questionnaireId,
                name: item.questionnaireName
            });
        }
    })
    return tempQs;
};


/**
 * Initializes selected content by matching program content with available content.
 * @param {Array<Object>} programContent - List of content from a program
 * @param {Array<Object>} allContent - List of all available content
 * @returns {Array<Object>} - Array of selected content with complete data
 */
export const initSelectedContent = (programContent, allContent) => {
    // check to see if content already exists in the state,
    const tempContent = [];

    programContent.forEach((item)=> {
        let existingContentIndex = allContent.findIndex(content => content.id === item.contentId);
        if (existingContentIndex !== -1){
            // if addProgram.content has a match in the content list, add to selectedContent
            tempContent.push(allContent[existingContentIndex]);
        } else {
            tempContent.push({
                id: item.contentId, 
                name: item.contentName
            })
        }
    });
    return tempContent;
};

/**
 * Initializes selected devices by matching program devices with available devices.
 * @param {Array<Object>} programDevices - List of devices from a program
 * @param {Array<Object>} allDevices - List of all available devices
 * @returns {Array<Object>} - Array of selected devices with complete data
 */
export const initSelectedDevices = (programDevices, allDevices) => {
    const tempDevices = [];
    programDevices.forEach((item) => {
        let existingDeviceIndex = allDevices.findIndex(device => device.deviceId === item.deviceId);
        if (existingDeviceIndex !== -1) {
            tempDevices.push(allDevices[existingDeviceIndex]);
        } else {
            tempDevices.push({
                id: item.deviceId, 
                name: item.deviceName
            })
        }
    });
    return tempDevices;
};

/** Perin Patch: Blood Pressure cannot repeat more often than every 15 minutes. */
export const MIN_BP_FREQUENCY_MINUTES = 15;
export const BP_MODALITY_TYPE = 'Blood Pressure';
export const PERIN_PATCH_MINUTE_OPTIONS = [5, 10, 15, 20, 25, 30, 35, 40, 45, 50, 55];

export const isBloodPressureModality = (modalityType) => modalityType === BP_MODALITY_TYPE;

/** True when the Perin Patch BP modality is currently scheduled (frequency > 0). */
export const isPerinPatchBpActive = (modalityState) =>
    Array.isArray(modalityState) &&
    modalityState.some(
        (m) => m?.deviceId === 1 && isBloodPressureModality(m?.modalityType) && m.frequency > 0
    );

export const getPerinPatchMinuteOptions = (modalityType) => {
    if (modalityType === BP_MODALITY_TYPE) {
        return PERIN_PATCH_MINUTE_OPTIONS.filter((m) => m >= MIN_BP_FREQUENCY_MINUTES);
    }
    return PERIN_PATCH_MINUTE_OPTIONS;
};

export const isInvalidBpMinuteFrequency = (mod) =>
    isBloodPressureModality(mod?.modalityType) &&
    mod.interval === 'Min' &&
    mod.frequency > 0 &&
    mod.frequency < MIN_BP_FREQUENCY_MINUTES;

export const shouldSkipBpOnApplyAllFrequency = (newFrequency, newInterval) =>
    newInterval === 'Min' && newFrequency > 0 && newFrequency < MIN_BP_FREQUENCY_MINUTES;

/**
 * Clamps a minute-interval frequency for Perin Patch (5-min steps, 5–55 or 15–55 for BP).
 */
export const clampPerinPatchMinuteFrequency = (value, modalityType) => {
    const isBp = modalityType === BP_MODALITY_TYPE;
    const min = isBp ? MIN_BP_FREQUENCY_MINUTES : 5;
    if (value < min) return min;
    if (value > 55) return 55;
    const rounded = Math.round(value / 5) * 5;
    return rounded < min ? min : rounded;
};

export const getDefaultMinFrequencyWhenSwitchingToMinutes = (currentFreq, modalityType) => {
    const min = modalityType === BP_MODALITY_TYPE ? MIN_BP_FREQUENCY_MINUTES : 5;
    if (currentFreq === 0 || currentFreq < min) return min;
    if (currentFreq > 55) return 55;
    return Math.round(currentFreq / 5) * 5;
};